import { FlashbarProps } from "@cloudscape-design/components";

export interface CustomFlashProps extends FlashbarProps.MessageDefinition {
    setNotifications: React.Dispatch<React.SetStateAction<FlashbarProps.MessageDefinition[]>>
}

export const flashbarMessage = ({header, type, content, setNotifications}: CustomFlashProps) => {
    const id = Math.random().toString();
    const item = {
        header: header,
        id: id,
        type: type,
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setNotifications(items => items.filter(item => item.id !== id)),
        content: content
    }
    return item
}

export const fetchAdditionalData = async (url) => {
    const response = await fetch(convertToHttps(url));
    const data = await response.json();
    return data;
  };

export function convertToHttps(url: string) {
    if (url.startsWith("http://")) {
      return url.replace("http://", "https://");
    }
    return url;
  }

  export const range = (start, stop) => [...Array(stop).keys()].slice(start);

// AI Prediction Types and Interfaces
export interface TeamStats {
    name: string;
    displayName: string;
    record: string;
    stats: Record<string, any>;
    previousSeasonStats?: Record<string, any>;
}

export interface MatchupPrediction {
    homeTeam: string;
    awayTeam: string;
    predictedWinner: string;
    confidence: number;
    reasoning: string;
    homeTeamStats: TeamStats;
    awayTeamStats: TeamStats;
}

// AI Prediction Service Functions
export const extractTeamStats = (team: Record<string, any>): TeamStats => {
    const stats = {};
    
    // Extract current season stats
    if (team.statsData && team.statsData.splits && team.statsData.splits.categories) {
        team.statsData.splits.categories.forEach(category => {
            stats[category.displayName] = {};
            category.stats.forEach(stat => {
                stats[category.displayName][stat.name] = {
                    displayName: stat.displayName,
                    value: stat.value,
                    description: stat.description
                };
            });
        });
    }

    return {
        name: team.name,
        displayName: team.displayName,
        record: team.recordData?.items?.[0]?.displayValue || "0-0",
        stats: stats
    };
};

export const formatStatsForAI = (homeTeam: TeamStats, awayTeam: TeamStats): string => {
    const formatTeamStats = (team: TeamStats) => {
        let statsText = `${team.displayName} (${team.record}):\n`;
        
        Object.keys(team.stats).forEach(category => {
            statsText += `  ${category}:\n`;
            Object.keys(team.stats[category]).forEach(statKey => {
                const stat = team.stats[category][statKey];
                statsText += `    - ${stat.displayName}: ${stat.value}\n`;
            });
        });
        
        return statsText;
    };

    return `NFL Matchup Analysis:

HOME TEAM:
${formatTeamStats(homeTeam)}

AWAY TEAM:
${formatTeamStats(awayTeam)}

You are analyzing a matchup between ${homeTeam.displayName} (home) and ${awayTeam.displayName} (away).

Please analyze this matchup and provide:
1. Your predicted winner (must be either "${homeTeam.displayName}" or "${awayTeam.displayName}")
2. Confidence level (0-100%)
3. Brief reasoning based on the statistics

IMPORTANT: Your response must be valid JSON only, no additional text. Use this exact format:
{
  "predictedWinner": "${homeTeam.displayName}",
  "confidence": 85,
  "reasoning": "Brief explanation of your prediction"
}`;
};

export const callOllamaAPI = async (prompt: string): Promise<MatchupPrediction | null> => {
    try {
        // TODO: Replace with your actual Ollama endpoint
        const OLLAMA_ENDPOINT = 'https://ai.kroshira.com/api/generate';
        const MODEL_NAME = 'llama3.2'; // Replace with your preferred model
        
        const response = await fetch(OLLAMA_ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                model: MODEL_NAME,
                prompt: prompt,
                stream: false,
                options: {
                    temperature: 0.7,
                    top_p: 0.9,
                }
            })
        });

        if (!response.ok) {
            throw new Error(`Ollama API error: ${response.status}`);
        }

        const data = await response.json();
        console.log('Raw AI response:', data.response);
        
        // Parse the AI response with improved error handling
        try {
            let responseText = data.response.trim();
            
            // Try to extract JSON from the response if it contains extra text
            const jsonMatch = responseText.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                responseText = jsonMatch[0];
            }
            
            console.log('Attempting to parse JSON:', responseText);
            const aiResponse = JSON.parse(responseText);
            
            // Validate required fields
            if (!aiResponse.predictedWinner || typeof aiResponse.confidence !== 'number') {
                console.error('AI response missing required fields:', aiResponse);
                return null;
            }
            
            return {
                homeTeam: '',
                awayTeam: '',
                predictedWinner: aiResponse.predictedWinner,
                confidence: aiResponse.confidence,
                reasoning: aiResponse.reasoning || 'No reasoning provided',
                homeTeamStats: {} as TeamStats,
                awayTeamStats: {} as TeamStats
            };
        } catch (parseError) {
            console.error('Failed to parse AI response:', parseError);
            console.error('Raw response was:', data.response);
            
            // Fallback: try to extract information manually
            const responseText = data.response.toLowerCase();
            let predictedWinner = 'Unknown';
            let confidence = 50;
            let reasoning = 'Could not parse AI response';
            
            // Simple pattern matching as fallback
            if (responseText.includes('eagles')) predictedWinner = 'Philadelphia Eagles';
            else if (responseText.includes('cowboys')) predictedWinner = 'Dallas Cowboys';
            else if (responseText.includes('patriots')) predictedWinner = 'New England Patriots';
            
            return {
                homeTeam: '',
                awayTeam: '',
                predictedWinner,
                confidence,
                reasoning: `Fallback parsing: ${reasoning}`,
                homeTeamStats: {} as TeamStats,
                awayTeamStats: {} as TeamStats
            };
        }
    } catch (error) {
        console.error('Error calling Ollama API:', error);
        return null;
    }
};

export const getPredictionForMatchup = async (
    homeTeam: Record<string, any>,
    awayTeam: Record<string, any>,
    teamsDict: Record<string, any>
): Promise<MatchupPrediction | null> => {
    try {
        console.log('=== DEBUGGING TEAM LOOKUP ===');
        console.log('Home Team from API:', homeTeam);
        console.log('Away Team from API:', awayTeam);
        console.log('Available teams in teamsDict:', Object.keys(teamsDict));
        
        // Try multiple lookup strategies for team matching
        const findTeamData = (team: Record<string, any>) => {
            // Strategy 1: Direct name match
            let teamData = teamsDict[team.name] || teamsDict[team.displayName];
            
            // Strategy 2: Try shortDisplayName
            if (!teamData && team.shortDisplayName) {
                teamData = teamsDict[team.shortDisplayName];
            }
            
            // Strategy 3: Try abbreviation
            if (!teamData && team.abbreviation) {
                teamData = teamsDict[team.abbreviation];
            }
            
            // Strategy 4: Search by partial name match
            if (!teamData) {
                const teamKeys = Object.keys(teamsDict);
                for (const key of teamKeys) {
                    const dictTeam = teamsDict[key];
                    if (dictTeam.displayName === team.displayName ||
                        dictTeam.name === team.name ||
                        dictTeam.shortDisplayName === team.shortDisplayName ||
                        dictTeam.abbreviation === team.abbreviation) {
                        teamData = dictTeam;
                        break;
                    }
                }
            }
            
            return teamData;
        };
        
        const homeTeamData = findTeamData(homeTeam);
        const awayTeamData = findTeamData(awayTeam);
        
        console.log('Found home team data:', homeTeamData ? homeTeamData.displayName : 'NOT FOUND');
        console.log('Found away team data:', awayTeamData ? awayTeamData.displayName : 'NOT FOUND');
        
        if (!homeTeamData || !awayTeamData) {
            console.warn('Team data not found for prediction');
            console.warn('Missing:', !homeTeamData ? 'home team' : 'away team');
            return null;
        }

        const homeStats = extractTeamStats(homeTeamData);
        const awayStats = extractTeamStats(awayTeamData);
        
        console.log('Home team stats extracted:', homeStats.displayName, homeStats.record);
        console.log('Away team stats extracted:', awayStats.displayName, awayStats.record);
        
        const prompt = formatStatsForAI(homeStats, awayStats);
        console.log('Generated prompt for AI:', prompt.substring(0, 200) + '...');
        
        const prediction = await callOllamaAPI(prompt);
        
        if (prediction) {
            return {
                ...prediction,
                homeTeam: homeTeam.displayName,
                awayTeam: awayTeam.displayName,
                homeTeamStats: homeStats,
                awayTeamStats: awayStats
            };
        }
        
        return null;
    } catch (error) {
        console.error('Error getting prediction for matchup:', error);
        return null;
    }
};